"use client";

import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { SlidersHorizontal, Gauge, Bug, ShieldCheck, Users, Compass, Clock, ArrowRight, Terminal, Cpu, Activity } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
interface SkillPoint {
  id: string;
  code: string;
  icon: React.ElementType;
  title: {
    vi: string;
    en: string;
  };
  description: {
    vi: string;
    en: string;
  };
  techDetail: {
    vi: string;
    en: string;
  };
}
const skillPoints: SkillPoint[] = [{
  id: "constraints",
  code: "VAL-01 // BOUNDARIES",
  icon: SlidersHorizontal,
  title: {
    vi: "Giải quyết trong giới hạn",
    en: "Solving under Constraints"
  },
  description: {
    vi: "Tối ưu hóa giải pháp kĩ thuật trong khuôn khổ khắt khe về kích thước robot, giới hạn linh kiện và quy tắc thi đấu.",
    en: "Optimizing technical solutions within strict physical dimensions, component limits, and challenge rules."
  },
  techDetail: {
    vi: "Rèn luyện tư duy thiết kế tối giản và tối đa hóa hiệu suất phần cứng.",
    en: "Develops minimal design thinking while maximizing hardware performance."
  }
}, {
  id: "testing",
  code: "VAL-02 // QA & VERIFY",
  icon: Gauge,
  title: {
    vi: "Kiểm thử hệ thống",
    en: "System Testing"
  },
  description: {
    vi: "Xây dựng các bài kiểm tra thực địa liên tục để đo lường độ chính xác của cảm biến và đáp ứng của động cơ.",
    en: "Building rigorous field test protocols to measure sensor accuracy and motor responsiveness."
  },
  techDetail: {
    vi: "Hình thành thói quen kiểm thử có hệ thống thay vì thử sai ngẫu nhiên.",
    en: "Builds systematic testing habits over random trial-and-error approaches."
  }
}, {
  id: "debugging",
  code: "VAL-03 // DIAGNOSTICS",
  icon: Bug,
  title: {
    vi: "Gỡ lỗi & Tối ưu",
    en: "Debugging & Optimization"
  },
  description: {
    vi: "Phân tích nhật ký hoạt động, cô lập sự cố phần cứng/phần mềm và sửa lỗi nhanh chóng dưới áp lực thời gian.",
    en: "Analyzing operational logs, isolating hardware/software issues, and fixing bugs rapidly under time pressure."
  },
  techDetail: {
    vi: "Tăng cường năng lực phân tích nguyên nhân gốc rễ (Root Cause Analysis).",
    en: "Enhances root-cause analysis and methodical problem isolation skills."
  }
}, {
  id: "reliability",
  code: "VAL-04 // STABILITY",
  icon: ShieldCheck,
  title: {
    vi: "Độ tin cậy & Vận hành",
    en: "System Reliability"
  },
  description: {
    vi: "Thiết kế cơ cấu bền bỉ và mã nguồn dự phòng để đảm bảo thiết bị vận hành ổn định trong mọi điều kiện.",
    en: "Engineering durable mechanisms and fail-safe code to ensure consistent execution in changing conditions."
  },
  techDetail: {
    vi: "Chuyển từ mô hình 'chạy thử thành công' sang 'vận hành ổn định lâu dài'.",
    en: "Shifts mindset from 'working once' to 'operating dependably every time'."
  }
}, {
  id: "teamwork",
  code: "VAL-05 // COLLABORATION",
  icon: Users,
  title: {
    vi: "Phối hợp đồng đội",
    en: "Teamwork & Integration"
  },
  description: {
    vi: "Phân chia vai trò rõ ràng giữa lập trình, cơ khí và chiến thuật, giao tiếp kĩ thuật hiệu quả khi tích hợp.",
    en: "Structuring clear roles between software, mechanics, and strategy with precise technical communication."
  },
  techDetail: {
    vi: "Phát triển kỹ năng làm việc nhóm thực chiến trong dự án kỹ thuật phức tạp.",
    en: "Builds practical engineering collaboration skills in complex team tasks."
  }
}, {
  id: "strategy",
  code: "VAL-06 // LOGIC & TACTICS",
  icon: Compass,
  title: {
    vi: "Tư duy chiến thuật",
    en: "Strategic Planning"
  },
  description: {
    vi: "Phân tích ma trận nhiệm vụ để lập lộ trình tính điểm tối ưu dựa trên năng lực thực tế của hệ thống.",
    en: "Analyzing mission matrices to devise optimal execution paths based on realistic system capacity."
  },
  techDetail: {
    vi: "Rèn luyện khả năng ra quyết định dựa trên dữ liệu và bài toán rủi ro.",
    en: "Refines data-driven decision making and pragmatic risk management."
  }
}, {
  id: "time-management",
  code: "VAL-07 // EXECUTION TIMELINE",
  icon: Clock,
  title: {
    vi: "Quản lý thời gian",
    en: "Time Management"
  },
  description: {
    vi: "Lập tiến độ chế tạo, phân bổ thời gian hoàn thiện và kiểm soát nhịp độ chuẩn bị trước ngày vận hành.",
    en: "Scheduling build milestones, managing iteration cycles, and pacing preparation before deployment."
  },
  techDetail: {
    vi: "Làm chủ phương pháp quản lý dự án kỹ thuật theo mốc thời gian thực.",
    en: "Mastering real-world project delivery and timeline management."
  }
}];
export default function CompetitionExperience() {
  const [lang, setLang] = useState<"vi" | "en">("vi");
  useEffect(() => {
    const getStoredLang = (): "vi" | "en" => {
      if (typeof window === "undefined") return "vi";
      const saved = localStorage.getItem("roboto_lang");
      return saved === "en" ? "en" : "vi";
    };
    setLang(getStoredLang());
    const handleLangChange = () => {
      setLang(getStoredLang());
    };
    window.addEventListener("roboto-lang-change", handleLangChange);
    window.addEventListener("storage", handleLangChange);
    return () => {
      window.removeEventListener("roboto-lang-change", handleLangChange);
      window.removeEventListener("storage", handleLangChange);
    };
  }, []);
  const isVi = lang === "vi";
  return <section id="competitionexperience" data-nav="light" className="relative py-20 lg:py-28 bg-background text-foreground overflow-hidden">
      {/* Background Technical Grid Deco */}
      <div className="aria-hidden:true pointer-events-none absolute inset-0 opacity-[0.03] dark:opacity-[0.06] bg-[radial-gradient(var(--border)_1px,transparent_1px)] [background-size:24px_24px]" aria-hidden="true" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 lg:mb-16 space-y-4">
          <Badge variant="outline" className="border-primary/30 text-primary bg-primary/5 px-3 py-1 text-xs tracking-wider uppercase font-mono">
            <Terminal className="size-3.5 mr-1.5" />
            {isVi ? "PHƯƠNG PHÁP HUẤN LUYỆN" : "ENGINEERING METHODOLOGY"}
          </Badge>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-serif tracking-tight text-foreground">
            {isVi ? "Trải nghiệm thi đấu" : "The Competition Experience"}
          </h2>

          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            {isVi ? "Môi trường thi đấu tại ROBOTO không hướng tới thành tích ngắn hạn, mà là phòng thí nghiệm thực tế giúp học viên rèn luyện tư duy kỹ thuật, bản lĩnh giải quyết vấn đề và tinh thần đồng đội." : "At ROBOTO, competition is not about short-term glory—it is a rigorous proving ground designed to hone engineering discipline, problem-solving resilience, and technical teamwork."}
          </p>
        </div>

        {/* Supporting Photography Banner: Deep Blue / White Technical Branding */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="mb-14 rounded-2xl overflow-hidden border border-slate-800 bg-slate-950 text-white shadow-xl relative group">
          <div className="grid grid-cols-1 lg:grid-cols-12 items-stretch">
            <div className="lg:col-span-7 relative min-h-[280px] sm:min-h-[340px] lg:min-h-[380px] overflow-hidden">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_teamwork_debugging.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_teamwork_debugging.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_teamwork_debugging.png-wvc-sizes" alt={isVi ? "Học viên ROBOTO thực hành gỡ lỗi và làm việc nhóm trong phòng lab" : "ROBOTO students collaborating on robotics debugging and teamwork"} loading="lazy" className="absolute inset-0 w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700 ease-out" />
              {/* Deep Blue / Technical Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/40 to-transparent lg:bg-gradient-to-r lg:from-transparent lg:via-slate-950/40 lg:to-slate-950" />
              
              {/* Technical Telemetry Badge */}
              <div className="absolute top-4 left-4 flex items-center gap-2 bg-slate-900/90 backdrop-blur-md px-3 py-1.5 rounded-md border border-slate-700/60 text-xs font-mono text-cyan-400">
                <Activity className="size-3.5 text-cyan-400 animate-pulse" />
                <span>ROBOTO_LAB // LIVE DEBUG & COLLABORATION</span>
              </div>
            </div>

            <div className="lg:col-span-5 p-6 sm:p-8 flex flex-col justify-center space-y-4 bg-slate-950/95 lg:bg-slate-950 border-t lg:border-t-0 lg:border-l border-slate-800">
              <div className="inline-flex items-center gap-2 text-xs font-mono text-cyan-400 uppercase tracking-wider bg-cyan-950/80 border border-cyan-800/60 px-3 py-1 rounded-md w-fit">
                <Cpu className="size-3.5 text-cyan-400" />
                {isVi ? "HOẠT ĐỘNG THỰC HÀNH" : "PRACTICAL LAB SESSION"}
              </div>

              <h3 className="text-xl sm:text-2xl font-bold font-serif text-white leading-tight">
                {isVi ? "Phân tích sự cố & Phối hợp nhóm" : "Collaborative Debugging & Problem Solving"}
              </h3>

              <p className="text-sm text-slate-300 leading-relaxed">
                {isVi ? "Hình ảnh thực tế học viên ROBOTO tập trung đo đạc điện áp, tối ưu hóa thuật toán điều khiển và cô lập lỗi phần cứng dưới sự hướng dẫn của chuyên gia kỹ thuật." : "ROBOTO students in action measuring sensor voltage, tuning control algorithms, and systematically isolating hardware faults under expert engineering guidance."}
              </p>

              <div className="grid grid-cols-2 gap-3 pt-3 text-xs font-mono border-t border-slate-800 text-slate-400">
                <div className="space-y-1">
                  <span className="text-slate-500 block uppercase tracking-wider text-[10px]">
                    {isVi ? "PHƯƠNG PHÁP" : "METHODOLOGY"}
                  </span>
                  <span className="text-slate-200 font-medium">
                    {isVi ? "Thử nghiệm & Gỡ lỗi" : "Iterative QA & Fix"}
                  </span>
                </div>
                <div className="space-y-1">
                  <span className="text-slate-500 block uppercase tracking-wider text-[10px]">
                    {isVi ? "MÔ HÌNH" : "TEAM FRAMEWORK"}
                  </span>
                  <span className="text-slate-200 font-medium">
                    {isVi ? "Tương tác đồng đội" : "Peer Engineering"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* 7 Technical Skill Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {skillPoints.map((point, index) => {
          const Icon = point.icon;
          const isLast = index === skillPoints.length - 1;
          return <motion.div key={point.id} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.35,
            delay: index * 0.06
          }} className={isLast ? "md:col-span-2 lg:col-span-1" : ""} data-index={index}>
                <Card className="h-full bg-card border-border/80 hover:border-primary/50 transition-all duration-300 shadow-sm hover:shadow-md flex flex-col justify-between group">
                  <CardHeader className="space-y-3 pb-3">
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-[10px] tracking-widest text-muted-foreground uppercase bg-muted px-2 py-0.5 rounded">
                        {point.code}
                      </span>
                      <div className="size-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                        <Icon className="size-5" />
                      </div>
                    </div>

                    <CardTitle className="text-xl font-semibold font-sans text-foreground group-hover:text-primary transition-colors">
                      {isVi ? point.title.vi : point.title.en}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="space-y-4 pt-0 flex-1 flex flex-col justify-between">
                    <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                      {isVi ? point.description.vi : point.description.en}
                    </CardDescription>

                    <div className="pt-3 border-t border-border/60 text-xs font-mono text-muted-foreground/90 flex items-start gap-2">
                      <Cpu className="size-3.5 text-primary shrink-0 mt-0.5" />
                      <span>{isVi ? point.techDetail.vi : point.techDetail.en}</span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </div>

        {/* Bottom CTA / Link Panel */}
        <div className="mt-16 text-center">
          <div className="inline-flex flex-col sm:flex-row items-center justify-between gap-6 p-6 sm:p-8 rounded-2xl bg-muted/50 border border-border/80 max-w-4xl mx-auto w-full">
            <div className="text-left space-y-1">
              <h3 className="text-lg font-semibold text-foreground">
                {isVi ? "Khám phá kết quả từ thực tiễn học tập" : "Explore learning outcomes & student projects"}
              </h3>
              <p className="text-sm text-muted-foreground">
                {isVi ? "Tìm hiểu các sản phẩm sáng tạo và hành trình phát triển của học viên ROBOTO." : "Discover student engineering projects and personal growth stories."}
              </p>
            </div>

            <Button size="lg" className="font-semibold shrink-0 group" asChild>
              <Link to="/StudentAchievementsStoriesPage">
                <span>{isVi ? "DỰ ÁN & THÀNH TỰU" : "Projects & Achievements"}</span>
                <ArrowRight className="size-4 ml-1.5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}